import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { BookMarked, User, Building2, Lightbulb, Wrench } from "lucide-react";
import SmartBackButton from "@/components/navigation/SmartBackButton";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { glossaryData, glossaryContent } from "@/data/glossary/index";
import { TERM_TYPE_LABELS, type GlossaryEntry } from "@/types/glossary";
import { Badge } from "@/components/ui/badge";

const getTypeIcon = (termType: GlossaryEntry["termType"]) => {
  switch (termType) {
    case "school":
      return BookMarked;
    case "person":
      return User;
    case "organization":
      return Building2;
    case "concept":
      return Lightbulb;
    case "technique":
      return Wrench;
    default:
      return BookMarked;
  }
};

const GlossarDetail = () => {
  const { termId } = useParams<{ termId: string }>();
  const entry = termId ? glossaryData[termId] : null;
  const content = termId ? glossaryContent[termId] : null;

  if (!entry) {
    return (
      <main className="min-h-screen bg-background text-foreground py-20">
        <div className="container mx-auto px-6 md:px-8 max-w-4xl text-center">
          <h1 className="text-2xl font-light mb-4">Begriff nicht gefunden</h1>
          <Link to="/mensch-im-fokus/yoga/wissen/glossar/" className="text-mif hover:underline">
            Zurück zum Glossar
          </Link>
        </div>
      </main>
    );
  }

  const TypeIcon = getTypeIcon(entry.termType);

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    name: entry.term,
    description: entry.shortDefinition,
    inDefinedTermSet: {
      "@type": "DefinedTermSet",
      name: "Yoga-Glossar",
      url: "https://nikola-knorr.de/mensch-im-fokus/yoga/wissen/glossar/",
    },
  };

  // Related terms lookup
  const relatedEntries = entry.relatedTerms
    ?.map((id) => glossaryData[id])
    .filter(Boolean) || [];

  return (
    <>
      <Helmet>
        <title>{entry.metaTitle}</title>
        <meta name="description" content={entry.metaDescription} />
        <link rel="canonical" href={`https://nikola-knorr.de/mensch-im-fokus/yoga/wissen/glossar/${entry.termId}/`} />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      <main className="min-h-screen bg-background text-foreground">
        {/* Hero Section */}
        <section className="py-16 md:py-20 bg-gradient-to-b from-mif/5 to-background">
          <div className="container mx-auto px-6 md:px-8 max-w-4xl">
            {/* Breadcrumb */}
            <SmartBackButton
              fallbackTo="/mensch-im-fokus/yoga/wissen/glossar/"
              fallbackLabel="Glossar"
              className="mb-10"
            />

            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-mif/10 flex items-center justify-center flex-shrink-0">
                <TypeIcon className="w-6 h-6 text-mif" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-light tracking-tight text-foreground">
                  {entry.term}
                </h1>
              </div>
            </div>

            <p className="text-lg text-muted-foreground max-w-2xl">
              {entry.shortDefinition}
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="py-12">
          <div className="container mx-auto px-6 md:px-8 max-w-4xl">
            {content ? (
              <article className="prose prose-neutral dark:prose-invert max-w-none prose-headings:font-light prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-p:leading-relaxed prose-a:text-mif prose-a:no-underline hover:prose-a:underline prose-ul:my-4 prose-li:my-1">
                <ReactMarkdown rehypePlugins={[rehypeRaw]}>{content}</ReactMarkdown>
              </article>
            ) : (
              <div className="bg-muted/30 rounded-xl p-8">
                <p className="text-muted-foreground">{entry.shortDefinition}</p>
              </div>
            )}

            {/* Related Terms */}
            {relatedEntries.length > 0 && (
              <div className="mt-12 pt-8 border-t border-border">
                <h2 className="text-xl font-light mb-4">Verwandte Begriffe</h2>
                <div className="flex flex-wrap gap-2">
                  {relatedEntries.map((related) => (
                    <Link
                      key={related.termId}
                      to={`/mensch-im-fokus/yoga/wissen/glossar/${related.termId}/`}
                      className="px-3 py-1.5 bg-muted rounded-full text-sm hover:bg-mif/10 hover:text-mif transition-colors"
                    >
                      {related.term}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Empfehlung – vom Begriff zur Praxis */}
            <aside className="mt-12 rounded-xl border border-mif/20 bg-mif/5 p-6 md:p-8">
              <h2 className="text-lg font-serif text-foreground mb-3">
                {entry.termType === "technique" || entry.termType === "concept"
                  ? "In der Praxis erfahren"
                  : "Die Viniyoga-Tradition lebendig erleben"}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                {entry.termType === "technique" || entry.termType === "concept"
                  ? "Yogische Konzepte und Techniken entfalten ihre Wirkung im eigenen Üben – am wirksamsten unter individueller Anleitung."
                  : "Diese Tradition lebt von der persönlichen Praxis und der Weitergabe von Mensch zu Mensch."}
              </p>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/mensch-im-fokus/yoga/einzelunterricht/" className="text-mif font-medium hover:underline">
                    Yoga-Einzelunterricht in Wiesbaden und online
                  </Link>
                  <span className="text-muted-foreground"> – individuelle Begleitung im eigenen Tempo</span>
                </li>
                <li>
                  <Link to="/mensch-im-fokus/yoga/gruppen/" className="text-mif font-medium hover:underline">
                    Yoga in der Gruppe &amp; Workshops in Wiesbaden
                  </Link>
                  <span className="text-muted-foreground"> – gemeinsam üben im Resonanzraum</span>
                </li>
                <li>
                  <Link to="/mensch-im-fokus/yoga/seminare/" className="text-mif font-medium hover:underline">
                    Yoga-Seminar auf Sylt
                  </Link>
                  <span className="text-muted-foreground"> – eine Woche Yoga, Atem und Regeneration in der Akademie Klappholttal</span>
                </li>
              </ul>
            </aside>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 border-t border-border">
          <div className="container mx-auto px-6 md:px-8 max-w-4xl">
            <div className="flex flex-col md:flex-row justify-between gap-8">
              <div className="space-y-2">
                <p className="font-medium text-foreground">Nikola Knorr</p>
                <p className="text-sm text-muted-foreground">
                  Sooderstraße 45<br />65193 Wiesbaden
                </p>
              </div>
              <div className="flex flex-col gap-2 text-sm">
                <Link to="/impressum/" className="text-muted-foreground hover:text-mif transition-colors">
                  Impressum
                </Link>
                <Link to="/datenschutz/" className="text-muted-foreground hover:text-mif transition-colors">
                  Datenschutz
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </main>
    </>
  );
};

export default GlossarDetail;
